import type { SidebarPanelContent } from '@/atoms/sidebarPanelAtoms';
import type { NextRouter } from 'next/router';
import type { MouseEvent } from 'react';
import { isSameTabNavigation, routePath } from './nav-item.utils';
import { startNavMenuSwitchTracking } from './nav-menu-switch-tracking';

interface CreateNavItemLinkClickHandlerParams {
  href: string;
  onClick?: (event?: React.MouseEvent) => void;
  sidebarPanelType?: SidebarPanelContent;
  setChatSidebarOpen: (open: boolean) => void;
  router: NextRouter;
  setPendingNavHref: (href: string | null) => void;
  testId?: string;
  shouldOpenPanelOnClick: boolean;
  setPanelPinned: (pinned: boolean) => void;
}

/**
 * Builds the internal-link `onClick` handler for `NavItem`. Extracted out of
 * the component body (kept it under the `max-lines` budget); the logic is
 * unchanged from its former inline form.
 */
export const createNavItemLinkClickHandler =
  ({
    href,
    onClick,
    sidebarPanelType,
    setChatSidebarOpen,
    router,
    setPendingNavHref,
    testId,
    shouldOpenPanelOnClick,
    setPanelPinned,
  }: CreateNavItemLinkClickHandlerParams) =>
  (event: MouseEvent<HTMLAnchorElement>): void => {
    if (!isSameTabNavigation(event)) return;
    (onClick as React.MouseEventHandler<HTMLAnchorElement> | undefined)?.(event);
    if (event.defaultPrevented) return;

    // Primary navigation dismisses the legacy Evidence ChatSidebar.
    // Evidence owns its destination state, so avoid a false→true
    // transition when the user selects Evidence itself.
    if (sidebarPanelType !== 'evidence') {
      setChatSidebarOpen(false);
    }
    // In-page query/hash changes need no page cover. Hash-only changes emit no
    // routeChangeComplete and would leave the pending href stuck.
    if (routePath(href) !== routePath(router.asPath)) {
      setPendingNavHref(routePath(href));
    }
    startNavMenuSwitchTracking({ href, router, testId });
    // On touch devices (can't hover), open sidebar in pinned mode when clicking
    if (shouldOpenPanelOnClick) {
      setPanelPinned(true);
    }
  };
